// src/components/catalog/ItemForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Upload, Image as ImageIcon, Trash2, Plus, Minus, Play,
  Car, Home, ShoppingCart, Utensils, Wrench, Tag, Link, Video,
  Star, GripVertical, Eye, RotateCcw
} from 'lucide-react';
import { catalogService } from '../../services/catalogService';
import { MediaService } from '../../services/mediaService';

export const ItemForm = ({ item, onSave, onClose, catalog }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'automotive',
    brand: '',
    price: '',
    description: '',
    location: '',
    status: 'active',
    featured: false,
    images: [], // Array of image URLs
    videos: [], // Array of video objects
    primaryImageIndex: 0, // Index of primary image
    imageUrl: '', // Legacy support - will be set to primary image
    specs: {},
    tags: [],
    externalLinks: [{ label: '', url: '' }]
  });

  const [mediaFiles, setMediaFiles] = useState({
    images: [], // Files to upload
    videos: []  // Files to upload
  });
  
  const [videoUrls, setVideoUrls] = useState(['', '']);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [newTag, setNewTag] = useState('');

  // Initialize form data when editing
  useEffect(() => {
    if (item) {
      const images = item.images || (item.imageUrl ? [item.imageUrl] : []);
      setFormData({
        ...item,
        images,
        videos: item.videos || [],
        primaryImageIndex: item.primaryImageIndex || 0,
        specs: item.specs || {},
        tags: item.tags || [],
        externalLinks: item.externalLinks || [{ label: '', url: '' }]
      });
      
      if (item.videos) {
        const urls = ['', ''];
        item.videos.forEach((video, index) => {
          if (index < 2 && (video.type === 'youtube' || video.type === 'vimeo')) {
            urls[index] = video.originalUrl || '';
          }
        });
        setVideoUrls(urls);
      }
    }
  }, [item]);

  // Category definitions
  const categoryFields = {
    automotive: {
      icon: Car,
      color: 'blue',
      fields: ['brand', 'year', 'mileage', 'fuelType', 'transmission', 'bodyType', 'color'],
      specs: {
        year: 'number',
        mileage: 'number',
        fuelType: 'select',
        transmission: 'select',
        bodyType: 'select',
        color: 'text'
      },
      selectOptions: {
        fuelType: ['Petrol', 'Diesel', 'Electric', 'Hybrid'],
        transmission: ['Manual', 'Automatic', 'CVT'],
        bodyType: ['Sedan', 'Hatchback', 'SUV', 'Truck', 'Coupe', 'Convertible', 'Wagon']
      }
    },
    'real-estate': {
      icon: Home,
      color: 'green',
      fields: ['propertyType', 'bedrooms', 'bathrooms', 'area', 'parking'],
      specs: {
        propertyType: 'select',
        bedrooms: 'number',
        bathrooms: 'number', 
        area: 'number',
        parking: 'number'
      },
      selectOptions: {
        propertyType: ['House', 'Apartment', 'Townhouse', 'Villa', 'Land', 'Commercial']
      }
    },
    retail: {
      icon: ShoppingCart,
      color: 'purple',
      fields: ['brand', 'size', 'color', 'material', 'condition'],
      specs: {
        size: 'text',
        color: 'text',
        material: 'text',
        condition: 'select'
      },
      selectOptions: {
        condition: ['New', 'Like New', 'Good', 'Fair', 'Poor']
      }
    },
    food: {
      icon: Utensils,
      color: 'orange',
      fields: ['cuisine', 'spiceLevel', 'dietary', 'servingSize'],
      specs: {
        cuisine: 'text',
        spiceLevel: 'select',
        dietary: 'text',
        servingSize: 'text'
      },
      selectOptions: {
        spiceLevel: ['Mild', 'Medium', 'Hot', 'Extra Hot']
      }
    },
    services: {
      icon: Wrench,
      color: 'gray',
      fields: ['serviceType', 'duration', 'availability', 'experience'],
      specs: {
        serviceType: 'text',
        duration: 'text',
        availability: 'text',
        experience: 'text'
      }
    }
  };

  // Image handling with drag and drop
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const currentImageCount = formData.images.length + mediaFiles.images.length;
    
    if (currentImageCount + files.length > 16) {
      alert('Maximum 16 images allowed');
      return;
    }
    
    setMediaFiles(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  const removeImage = (index, isUploaded = false) => {
    if (isUploaded) {
      const newImages = formData.images.filter((_, i) => i !== index);
      const newPrimaryIndex = formData.primaryImageIndex >= newImages.length 
        ? Math.max(0, newImages.length - 1) 
        : formData.primaryImageIndex === index 
          ? 0 
          : formData.primaryImageIndex > index 
            ? formData.primaryImageIndex - 1 
            : formData.primaryImageIndex;

      setFormData(prev => ({
        ...prev,
        images: newImages,
        primaryImageIndex: newPrimaryIndex
      }));
    } else {
      setMediaFiles(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const removeVideoFile = (index) => {
    setMediaFiles(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const setPrimaryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryImageIndex: index
    }));
  };

  const moveImage = useCallback((dragIndex, dropIndex) => {
    const newImages = [...formData.images];
    const draggedImage = newImages[dragIndex];
    newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    let newPrimaryIndex = formData.primaryImageIndex;
    if (formData.primaryImageIndex === dragIndex) {
      newPrimaryIndex = dropIndex;
    } else if (dragIndex < formData.primaryImageIndex && dropIndex >= formData.primaryImageIndex) {
      newPrimaryIndex = formData.primaryImageIndex - 1;
    } else if (dragIndex > formData.primaryImageIndex && dropIndex <= formData.primaryImageIndex) {
      newPrimaryIndex = formData.primaryImageIndex + 1;
    }

    setFormData(prev => ({
      ...prev,
      images: newImages,
      primaryImageIndex: newPrimaryIndex
    }));
  }, [formData.images, formData.primaryImageIndex]);

  // Video handling
  const handleVideoSelect = (event) => {
    const files = Array.from(event.target.files);
    const currentVideoCount = formData.videos.filter(v => v.type === 'video').length + mediaFiles.videos.length;
    
    if (currentVideoCount + files.length > 2) {
      alert('Maximum 2 videos allowed');
      return;
    }
    
    setMediaFiles(prev => ({
      ...prev,
      videos: [...prev.videos, ...files]
    }));
  };

  const handleVideoUrlChange = (index, url) => {
    const newUrls = [...videoUrls];
    newUrls[index] = url;
    setVideoUrls(newUrls);
  };

  const addVideoUrl = (index) => {
    const url = videoUrls[index].trim();
    if (!url) return;
    
    const processedVideo = catalogService.processVideoUrl(url);
    if (processedVideo.type === 'unknown') {
      alert('Invalid video URL. Please use YouTube or Vimeo URLs.');
      return;
    }
    
    const currentUrlVideos = formData.videos.filter(v => v.type !== 'video');
    if (currentUrlVideos.length >= 2) {
      alert('Maximum 2 videos allowed');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      videos: [...prev.videos.filter(v => v.originalUrl !== url), processedVideo]
    }));
    
    const newUrls = [...videoUrls];
    newUrls[index] = '';
    setVideoUrls(newUrls);
  };

  // Upload all media files
  const uploadAllMedia = async () => {
    setUploading(true);
    setUploadProgress(0);
    
    try {
      let newImages = [...formData.images];
      let newVideos = [...formData.videos];
      
      if (mediaFiles.images.length > 0) {
        const imageResults = await catalogService.uploadMultipleImages(
          mediaFiles.images,
          `catalogs/${catalog.id}/items`,
          (completed, total) => {
            setUploadProgress(Math.round((completed / total) * 50));
          }
        );
        
        const successfulUploads = imageResults.filter(result => !result.error);
        newImages = [...newImages, ...successfulUploads.map(result => result.url)];
      }
      
      if (mediaFiles.videos.length > 0) {
        for (let i = 0; i < mediaFiles.videos.length; i++) {
          const videoResult = await catalogService.uploadVideo(
            mediaFiles.videos[i],
            `catalogs/${catalog.id}/items`
          );
          newVideos.push(videoResult);
          setUploadProgress(50 + Math.round(((i + 1) / mediaFiles.videos.length) * 50));
        }
      }
      
      return { images: newImages, videos: newVideos };
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { images, videos } = await uploadAllMedia();
      
      const itemData = {
        ...formData,
        images,
        videos,
        primaryImageIndex: formData.primaryImageIndex,
        imageUrl: images[formData.primaryImageIndex] || images[0] || '', // Set primary image
        price: formData.price ? parseFloat(formData.price) : null
      };

      await onSave(itemData);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const currentCategory = categoryFields[formData.category] || categoryFields.automotive;
  const CategoryIcon = currentCategory.icon;
  const allImages = [...formData.images, ...mediaFiles.images.map(file => URL.createObjectURL(file))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-${currentCategory.color}-50 to-${currentCategory.color}-100`}>
          <div className="flex items-center gap-4">
            <div className={`p-3 bg-${currentCategory.color}-500 text-white rounded-xl shadow-lg`}>
              <CategoryIcon className="h-7 w-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {item ? 'Edit Item' : 'Add New Item'}
              </h2>
              <p className="text-gray-600">
                {item ? 'Update item information and media' : 'Create a new catalog item with rich media'}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-700" />
          </button>
        </div>

        {/* Enhanced Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex space-x-1 px-6">
            {[
              { id: 'basic', label: 'Basic Info', icon: '📝' },
              { id: 'media', label: 'Images & Videos', icon: '🎨' },
              { id: 'specs', label: 'Details & Tags', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-6 font-medium text-sm transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? `border-${currentCategory.color}-500 text-${currentCategory.color}-600 bg-white`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white hover:bg-opacity-50'
                }`}
              >
                <span className="text-base">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-8">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-8">
                {/* Primary Fields */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="text-xl">📋</span>
                    Primary Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Item Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg font-medium"
                        placeholder="Enter a descriptive item name..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value="automotive">🚗 Automotive</option>
                        <option value="real-estate">🏠 Real Estate</option>
                        <option value="retail">🛍️ Retail</option>
                        <option value="food">🍽️ Food & Beverage</option>
                        <option value="services">🔧 Services</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">R</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Brand
                      </label>
                      <input
                        type="text"
                        value={formData.brand}
                        onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter brand name..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter location..."
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        placeholder="Describe your item in detail. Include key features, condition, and any important information..."
                      />
                    </div>
                  </div>
                </div>

                {/* Status & Settings */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="text-xl">⚡</span>
                    Status & Settings
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        <option value="active">✅ Active</option>
                        <option value="draft">📝 Draft</option>
                        <option value="sold">🔒 Sold</option>
                        <option value="inactive">⏸️ Inactive</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="text-sm font-semibold text-gray-700">Featured Item</span>
                          <p className="text-xs text-gray-500">Highlight this item in listings</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Media Tab */}
            {activeTab === 'media' && (
              <div className="space-y-8">
                {/* Images Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="text-xl">🖼️</span>
                      Images
                      <span className="text-sm font-normal text-gray-500">
                        ({allImages.length}/16)
                      </span>
                    </h3>
                    
                    {allImages.length > 0 && (
                      <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                        Primary: Image {formData.primaryImageIndex + 1}
                      </div>
                    )}
                  </div>

                  {/* Image Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="max-w-md mx-auto">
                      <ImageIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                      <div className="mb-4">
                        <label 
                          htmlFor="images-upload" 
                          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold cursor-pointer transition-colors"
                        >
                          <Upload className="h-5 w-5" />
                          Choose Images
                        </label>
                        <input
                          id="images-upload"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageSelect}
                          className="hidden"
                          disabled={allImages.length >= 16}
                        />
                      </div>
                      <div className="text-sm space-y-1">
                        <p className="text-gray-600">
                          <strong>Drag & drop</strong> or <strong>click to upload</strong>
                        </p>
                        <p className="text-gray-500">
                          PNG, JPG, GIF up to 10MB each • Maximum 16 images
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Image Preview Grid with Primary Selection */}
                  {allImages.length > 0 && (
                    <div>
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 text-blue-700 mb-2">
                          <Eye className="h-4 w-4" />
                          <span className="font-semibold text-sm">Primary Image Preview</span>
                        </div>
                        <p className="text-blue-600 text-sm mb-3">
                          This image will appear on catalog cards and as the main image. Click "Set as Primary" on any image below to change it.
                        </p>
                        <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-blue-300 shadow-md">
                          <img
                            src={allImages[formData.primaryImageIndex]}
                            alt="Primary image"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {/* Existing uploaded images */}
                        {formData.images.map((imageUrl, index) => (
                          <div 
                            key={`uploaded-${index}`} 
                            className={`relative group border-2 rounded-xl overflow-hidden transition-all ${
                              index === formData.primaryImageIndex 
                                ? 'border-blue-500 ring-2 ring-blue-200' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="aspect-square">
                              <img
                                src={imageUrl}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Overlay Controls */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                {index !== formData.primaryImageIndex && (
                                  <button
                                    type="button"
                                    onClick={() => setPrimaryImage(index)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg text-xs font-semibold transition-colors"
                                    title="Set as Primary"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeImage(index, true)}
                                  className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                                  title="Remove Image"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                            {/* Primary Badge */}
                            {index === formData.primaryImageIndex && (
                              <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                                <Star className="h-3 w-3 fill-current" />
                                Primary
                              </div>
                            )}

                            {/* Image Number */}
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                              {index + 1}
                            </div>
                          </div>
                        ))}
                        
                        {/* New image files to upload */}
                        {mediaFiles.images.map((file, index) => (
                          <div 
                            key={`new-${index}`} 
                            className="relative group border-2 border-gray-200 rounded-xl overflow-hidden hover:border-yellow-400 transition-all"
                          >
                            <div className="aspect-square">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                              <button
                                type="button"
                                onClick={() => removeImage(index, false)}
                                className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-all"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="absolute top-2 left-2 bg-yellow-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                              New
                            </div>

                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                              {formData.images.length + index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Videos Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="text-xl">🎥</span>
                      Videos
                      <span className="text-sm font-normal text-gray-500">
                        ({formData.videos.length + mediaFiles.videos.length}/2)
                      </span>
                    </h3>
                  </div>

                  {/* YouTube/Vimeo URL Inputs */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Link className="h-4 w-4" />
                      Add from YouTube or Vimeo
                    </h4>
                    <div className="space-y-3">
                      {videoUrls.map((url, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="flex-1">
                            <input
                              type="url"
                              value={url}
                              onChange={(e) => handleVideoUrlChange(index, e.target.value)}
                              placeholder={`Paste YouTube or Vimeo URL here...`}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              disabled={formData.videos.filter(v => v.type !== 'video').length >= 2}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => addVideoUrl(index)}
                            disabled={!url.trim() || formData.videos.filter(v => v.type !== 'video').length >= 2}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-semibold transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Video File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6 bg-gray-50">
                    <Video className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <div className="mb-4">
                      <label 
                        htmlFor="videos-upload" 
                        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold cursor-pointer transition-colors"
                      >
                        <Upload className="h-5 w-5" />
                        Upload Videos
                      </label>
                      <input
                        id="videos-upload"
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleVideoSelect}
                        className="hidden"
                        disabled={formData.videos.filter(v => v.type === 'video').length + mediaFiles.videos.length >= 2}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      MP4, WebM, MOV up to 100MB each • Maximum 2 videos
                    </p>
                  </div>

                  {/* Video Preview Grid */}
                  {(formData.videos.length > 0 || mediaFiles.videos.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Existing videos */}
                      {formData.videos.map((video, index) => (
                        <div key={`video-${index}`} className="relative group bg-gray-100 rounded-xl p-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Play className="h-4 w-4 text-purple-600" />
                              <span className="text-sm font-semibold capitalize text-gray-700">
                                {video.type} Video
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                videos: prev.videos.filter((_, i) => i !== index)
                              }))}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="aspect-video rounded-lg overflow-hidden bg-gray-200">
                            {video.type === 'youtube' || video.type === 'vimeo' ? (
                              <div className="relative w-full h-full">
                                <img
                                  src={video.thumbnail}
                                  alt="Video thumbnail"
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="bg-black bg-opacity-60 rounded-full p-3">
                                    <Play className="h-6 w-6 text-white fill-current" />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="text-center">
                                  <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-600 font-medium">Video File</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* New video files */}
                      {mediaFiles.videos.map((file, index) => (
                        <div key={`video-file-${index}`} className="relative group bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Upload className="h-4 w-4 text-yellow-600" />
                              <span className="text-sm font-semibold text-gray-700">New Upload</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVideoFile(index)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="aspect-video rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                            <div className="text-center">
                              <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-700 font-semibold">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / (1024*1024)).toFixed(1)} MB</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Specs Tab */}
            {activeTab === 'specs' && (
              <div className="space-y-8">
                {/* Category-specific specs */}
                {currentCategory.specs && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <CategoryIcon className="h-5 w-5" />
                      {formData.category.charAt(0).toUpperCase() + formData.category.slice(1)} Specifications
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {Object.entries(currentCategory.specs).map(([key, type]) => (
                        <div key={key}>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </label>
                          {type === 'select' ? (
                            <select
                              value={formData.specs[key] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                specs: { ...prev.specs, [key]: e.target.value }
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                              <option value="">Select {key}</option>
                              {currentCategory.selectOptions?.[key]?.map(option => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={type}
                              value={formData.specs[key] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                specs: { ...prev.specs, [key]: e.target.value }
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              placeholder={`Enter ${key}...`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    Tags
                  </h3>
                  
                  <div className="flex gap-3 mb-4">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag (e.g., luxury, vintage, eco-friendly)"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
                            setFormData(prev => ({
                              ...prev,
                              tags: [...prev.tags, newTag.trim()]
                            }));
                            setNewTag('');
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...prev.tags, newTag.trim()]
                          }));
                          setNewTag('');
                        }
                      }}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium"
                      >
                        <Tag className="h-3 w-3" />
                        {tag}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            tags: prev.tags.filter((_, i) => i !== index)
                          }))}
                          className="ml-1 hover:text-blue-600 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* External Links */}
                {catalog.allowExternalLinks && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                      <Link className="h-5 w-5" />
                      External Links
                    </h3>
                    
                    <div className="space-y-4">
                      {formData.externalLinks.map((link, index) => (
                        <div key={index} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                          <input
                            type="text"
                            value={link.label}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              externalLinks: prev.externalLinks.map((l, i) => 
                                i === index ? { ...l, label: e.target.value } : l
                              )
                            }))}
                            placeholder="Link title (e.g., Manufacturer Page)"
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          <input
                            type="url"
                            value={link.url}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              externalLinks: prev.externalLinks.map((l, i) => 
                                i === index ? { ...l, url: e.target.value } : l
                              )
                            }))}
                            placeholder="https://example.com"
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              externalLinks: prev.externalLinks.filter((_, i) => i !== index)
                            }))}
                            className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          externalLinks: [...prev.externalLinks, { label: '', url: '' }]
                        }))}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-gray-600 hover:text-blue-600 flex items-center justify-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add External Link
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="mb-6">
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-blue-700 font-semibold">Uploading media files...</p>
                      <p className="text-blue-600 text-sm">{uploadProgress}% complete</p>
                    </div>
                  </div>
                  <div className="bg-blue-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-blue-600 rounded-full h-3 transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Enhanced Submit Buttons */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-semibold transition-colors"
              disabled={saving || uploading}
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={saving || uploading || !formData.name.trim()}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Saving Item...
                </>
              ) : uploading ? (
                <>
                  <Upload className="h-5 w-5" />
                  Uploading...
                </>
              ) : (
                <>
                  Save Item
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};